/**
 * Story Plot Suggestions
 *
 * Curated, age-appropriate story plot suggestions for the story creation wizard.
 * These provide inspiration for users who need help coming up with story ideas.
 */

import { StoryPlotSuggestion } from '../types'

export const storyPlotSuggestions: StoryPlotSuggestion[] = [
  // BEDTIME (4)
  {
    id: 'sleepy-stars-goodnight',
    title: 'Sleepy Stars Say Goodnight',
    plot: 'A little child looks out the window at bedtime and sees the stars twinkling. One by one, each star says goodnight and closes its eyes. The child yawns, snuggles into bed, and falls asleep under the starry sky.',
    category: 'bedtime',
    themes: ['bedtime routine', 'stars', 'peaceful sleep', 'nighttime'],
    ageRange: [2, 4],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'Friendly twinkling stars with sleepy faces, a glowing moon',
    recommendedIllustrations: 5,
    recommendedPages: 10,
    aiOptimized: true,
  },
  {
    id: 'moons-sleepy-song',
    title: 'The Moon\'s Sleepy Song',
    plot: 'A child cannot fall asleep. The friendly moon peeks through the window and sings a soft song. The song makes the trees sway, the flowers close, and the child feel cozy. Soon everyone is fast asleep.',
    category: 'bedtime',
    themes: ['lullaby', 'moon', 'peaceful', 'nature', 'sleep'],
    ageRange: [2, 4],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'A kind smiling moon, swaying trees, closing flowers',
    recommendedIllustrations: 5,
    recommendedPages: 10,
    aiOptimized: true,
  },
  {
    id: 'dream-cloud-ride',
    title: 'A Ride on a Dream Cloud',
    plot: 'A child closes their eyes at bedtime and a fluffy cloud floats by. The child hops on and the cloud carries them gently over sleeping houses and quiet forests. The cloud brings the child back to bed feeling happy and calm.',
    category: 'bedtime',
    themes: ['dreams', 'imagination', 'gentle adventure', 'sleep'],
    ageRange: [2, 4],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'A soft fluffy dream cloud, sleeping birds, quiet houses below',
    recommendedIllustrations: 5,
    recommendedPages: 10,
    aiOptimized: true,
  },
  {
    id: 'nighttime-lullaby',
    title: 'The Nighttime Lullaby',
    plot: 'A child hears a beautiful melody drifting through the window. Following the sound, they discover fireflies creating music with their glowing lights. The child joins in, humming along until sleepy eyes close.',
    category: 'bedtime',
    themes: ['music', 'fireflies', 'peaceful', 'nature'],
    ageRange: [3, 6],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'Magical musical fireflies, gentle night breeze',
    recommendedIllustrations: 6,
    recommendedPages: 12,
    aiOptimized: true,
  },

  // ADVENTURE (4)
  {
    id: 'backyard-safari',
    title: 'Backyard Safari',
    plot: 'A child pretends to be an explorer in the backyard. They find a fuzzy caterpillar, a hopping frog, and a pretty butterfly. Each creature says hello before the child goes inside for a snack.',
    category: 'adventure',
    themes: ['exploration', 'nature', 'insects', 'imagination', 'backyard'],
    ageRange: [2, 4],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'A friendly caterpillar, a hopping frog, a colorful butterfly',
    recommendedIllustrations: 6,
    recommendedPages: 12,
    aiOptimized: true,
  },
  {
    id: 'treasure-map-adventure',
    title: 'The Treasure Map Adventure',
    plot: 'A child finds a mysterious map in the attic. Following the clues through the house and garden, they discover that the real treasure is a box of old family photos and stories from grandparents.',
    category: 'adventure',
    themes: ['treasure hunt', 'family', 'discovery', 'memories'],
    ageRange: [4, 7],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'Helpful family members, a wise grandparent',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'rainforest-rescue',
    title: 'Rainforest Rescue',
    plot: 'Deep in the rainforest, a young explorer discovers a baby bird that has fallen from its nest. With help from forest friends—a wise owl, a helpful monkey, and a brave toucan—they work together to return the baby to its family.',
    category: 'adventure',
    themes: ['helping others', 'teamwork', 'nature', 'wildlife'],
    ageRange: [5, 8],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child explorer',
    auxiliaryCharacters: 'Baby bird, wise owl, helpful monkey, brave toucan',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'mountain-climb',
    title: 'The Big Mountain Climb',
    plot: 'A child decides to climb the big hill behind their house. Along the way, they face challenges but learn that taking breaks, staying positive, and asking for help makes any journey possible.',
    category: 'adventure',
    themes: ['perseverance', 'determination', 'nature', 'achievement'],
    ageRange: [4, 7],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child protagonist',
    auxiliaryCharacters: 'Encouraging woodland creatures, a supportive parent at the summit',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },

  // FRIENDSHIP (4)
  {
    id: 'new-friend-playground',
    title: 'A New Friend at the Playground',
    plot: 'A shy child watches others play at the playground. A friendly child invites them to join in a game. Together they swing, slide, and laugh, learning that making friends can be as simple as saying hello.',
    category: 'friendship',
    themes: ['friendship', 'sharing', 'kindness', 'playground'],
    ageRange: [3, 5],
    characterTypeHints: ['human'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 shy child + 1 friendly child',
    auxiliaryCharacters: 'Other playground children, a helpful parent',
    recommendedIllustrations: 6,
    recommendedPages: 12,
    aiOptimized: true,
  },
  {
    id: 'sharing-umbrella',
    title: 'The Sharing Umbrella',
    plot: 'On a rainy day, a child with a big umbrella sees a classmate getting wet. They share their umbrella for the walk home, discovering they both love the same games. A rainy day becomes the start of a beautiful friendship.',
    category: 'friendship',
    themes: ['sharing', 'kindness', 'weather', 'new friends'],
    ageRange: [4, 7],
    characterTypeHints: ['human'],
    mainCharacterCount: 2,
    mainCharacterRoles: '2 child protagonists',
    auxiliaryCharacters: 'Smiling raindrops, puddles to splash in',
    recommendedIllustrations: 6,
    recommendedPages: 12,
    aiOptimized: true,
  },
  {
    id: 'different-friends',
    title: 'Different Friends, Same Fun',
    plot: 'A child meets someone who looks and speaks differently. At first they are unsure how to communicate, but they discover that smiles, games, and kindness are universal languages that make the best friendships.',
    category: 'friendship',
    themes: ['diversity', 'inclusion', 'communication', 'kindness'],
    ageRange: [4, 8],
    characterTypeHints: ['human'],
    mainCharacterCount: 2,
    mainCharacterRoles: '2 children from different backgrounds',
    auxiliaryCharacters: 'Supportive adults, playground friends',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },
  {
    id: 'friend-in-need',
    title: 'A Friend in Need',
    plot: 'When a classmate loses their favorite toy, our hero helps search everywhere. Even when they find it broken, they work together to fix it, learning that true friends help each other through difficult times.',
    category: 'friendship',
    themes: ['helping', 'empathy', 'problem-solving', 'support'],
    ageRange: [5, 8],
    characterTypeHints: ['human'],
    mainCharacterCount: 2,
    mainCharacterRoles: '2 friends working together',
    auxiliaryCharacters: 'Understanding teacher, supportive classmates',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },

  // ANIMALS (4)
  {
    id: 'puppy-first-walk',
    title: 'Puppy\'s First Walk',
    plot: 'A family gets a new puppy. The child takes the puppy for their first walk, encountering butterflies, birds, and other dogs. The puppy is nervous at first but grows brave with their best friend by their side.',
    category: 'animals',
    themes: ['pets', 'responsibility', 'bravery', 'bonding'],
    ageRange: [3, 6],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 child + 1 puppy',
    auxiliaryCharacters: 'Friendly neighborhood animals, butterflies',
    recommendedIllustrations: 6,
    recommendedPages: 12,
    aiOptimized: true,
  },
  {
    id: 'farm-adventure',
    title: 'A Day at Grandma\'s Farm',
    plot: 'A child visits their grandparents\' farm and meets all the animals—chickens, cows, pigs, and horses. Each animal teaches them something special about life on the farm and the joy of caring for others.',
    category: 'animals',
    themes: ['farm animals', 'grandparents', 'learning', 'responsibility'],
    ageRange: [3, 6],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child visiting the farm',
    auxiliaryCharacters: 'Grandparents, farm animals',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'lost-kitten',
    title: 'The Lost Kitten',
    plot: 'A child finds a tiny, scared kitten in the rain. They bring it home, dry it off, and give it food. Together they put up "found" posters and learn about responsibility while caring for the kitten until its owner is found.',
    category: 'animals',
    themes: ['kindness', 'responsibility', 'caring', 'pets'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 compassionate child',
    auxiliaryCharacters: 'Lost kitten, helpful parents, grateful owner',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },
  {
    id: 'ocean-friends',
    title: 'Ocean Friends',
    plot: 'While snorkeling on vacation, a child makes friends with colorful fish, a gentle sea turtle, and a playful dolphin. They learn about ocean life and the importance of keeping the ocean clean.',
    category: 'animals',
    themes: ['ocean', 'marine life', 'conservation', 'exploration'],
    ageRange: [5, 8],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 curious child explorer',
    auxiliaryCharacters: 'Sea turtle, dolphin, colorful fish, coral',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },

  // MAGICAL (4)
  {
    id: 'magic-paintbrush',
    title: 'The Magic Paintbrush',
    plot: 'A child discovers a paintbrush that brings drawings to life. They paint a puppy, a rainbow, and a castle. But when things get out of hand, they learn that some magic works best in imagination.',
    category: 'magical',
    themes: ['creativity', 'imagination', 'responsibility', 'art'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 creative child artist',
    auxiliaryCharacters: 'Painted puppy, living rainbow, friendly castle characters',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'fairy-garden',
    title: 'The Secret Fairy Garden',
    plot: 'In the corner of the backyard, a child discovers a tiny door in a tree trunk. Inside lives a fairy who needs help making her garden bloom again. Together they plant flowers and restore the magic.',
    category: 'magical',
    themes: ['fairies', 'gardening', 'helping others', 'nature'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 child + 1 friendly fairy',
    auxiliaryCharacters: 'Garden creatures, magical flowers',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'dragon-friend',
    title: 'My Dragon Friend',
    plot: 'A friendly dragon moves into the neighborhood, but everyone is scared. One brave child decides to say hello and discovers the dragon just wants friends. Soon the whole neighborhood learns that different doesn\'t mean scary.',
    category: 'magical',
    themes: ['acceptance', 'bravery', 'friendship', 'overcoming fear'],
    ageRange: [5, 8],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 brave child + 1 friendly dragon',
    auxiliaryCharacters: 'Nervous neighbors, eventually welcoming community',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'wishing-star',
    title: 'The Wishing Star',
    plot: 'A child makes a wish on a shooting star, and the star comes to visit! The star shows them that the best wishes—for love, friendship, and happiness—are already all around them.',
    category: 'magical',
    themes: ['gratitude', 'wishes', 'appreciation', 'magic'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 child + 1 friendly star',
    auxiliaryCharacters: 'Family members, friends the star reveals',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },

  // LEARNING (4)
  {
    id: 'counting-adventure',
    title: 'Counting to Ten with Friends',
    plot: 'A child goes on a walk and counts everything they see—1 dog, 2 birds, 3 flowers, all the way to 10. Each number brings a fun discovery and a new friend to count with.',
    category: 'learning',
    themes: ['counting', 'numbers', 'observation', 'nature walk'],
    ageRange: [2, 4],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 curious child learning to count',
    auxiliaryCharacters: 'Animals and objects representing each number',
    recommendedIllustrations: 10,
    recommendedPages: 12,
    aiOptimized: true,
  },
  {
    id: 'color-discovery',
    title: 'The Color Discovery Day',
    plot: 'A child wakes up to find all the colors missing from their world! They go on a quest to find red in apples, blue in the sky, and yellow in the sun, learning about colors along the way.',
    category: 'learning',
    themes: ['colors', 'discovery', 'observation', 'art'],
    ageRange: [2, 5],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child on a color quest',
    auxiliaryCharacters: 'Colorful objects and nature elements',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'alphabet-journey',
    title: 'The Alphabet Journey',
    plot: 'A child follows a trail of letters that lead them through a magical land. From A is for Apple tree to Z is for Zebra, each letter brings a new adventure and a new word to learn.',
    category: 'learning',
    themes: ['alphabet', 'letters', 'vocabulary', 'adventure'],
    ageRange: [3, 6],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child learning the alphabet',
    auxiliaryCharacters: 'Objects and animals for each letter',
    recommendedIllustrations: 13,
    recommendedPages: 26,
    aiOptimized: true,
  },
  {
    id: 'seasons-story',
    title: 'The Four Seasons Story',
    plot: 'A magical tree in the backyard shows a child what happens in each season. Spring brings flowers, summer brings sunshine, fall brings colorful leaves, and winter brings snow. Each season is special in its own way.',
    category: 'learning',
    themes: ['seasons', 'nature', 'time', 'change'],
    ageRange: [3, 6],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 curious child',
    auxiliaryCharacters: 'Magical tree, season-specific animals and elements',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },

  // SPACE (4)
  {
    id: 'moon-visit',
    title: 'A Visit to the Moon',
    plot: 'One night, a child dreams of visiting the moon. They hop aboard a rocket ship made of pillows and fly through the stars. On the moon, they meet friendly moon creatures and learn about the night sky.',
    category: 'space',
    themes: ['space', 'dreams', 'imagination', 'moon'],
    ageRange: [3, 6],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 dreaming child astronaut',
    auxiliaryCharacters: 'Friendly moon creatures, twinkling stars',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },
  {
    id: 'planet-tour',
    title: 'The Solar System Tour',
    plot: 'A friendly alien takes a child on a tour of the solar system. They visit each planet, learning one cool fact about each. By the end, the child knows more about space than ever before!',
    category: 'space',
    themes: ['solar system', 'planets', 'science', 'friendship'],
    ageRange: [5, 8],
    characterTypeHints: ['human', 'robot'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 curious child + 1 friendly alien guide',
    auxiliaryCharacters: 'Planets with personalities, stars',
    recommendedIllustrations: 10,
    recommendedPages: 20,
    aiOptimized: true,
  },
  {
    id: 'star-collector',
    title: 'The Star Collector',
    plot: 'A child notices a star fall from the sky. They gently catch it and discover it needs to get back to its constellation family. With courage and kindness, they help the star find its way home.',
    category: 'space',
    themes: ['stars', 'helping others', 'family', 'courage'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 kind child + 1 lost star',
    auxiliaryCharacters: 'Constellation families, the moon',
    recommendedIllustrations: 7,
    recommendedPages: 14,
    aiOptimized: true,
  },
  {
    id: 'rocket-builder',
    title: 'Building a Rocket',
    plot: 'A child dreams of going to space but has no rocket. With creativity and help from family, they build a pretend rocket from cardboard boxes. In their imagination, they blast off on the greatest adventure ever!',
    category: 'space',
    themes: ['creativity', 'imagination', 'family', 'building'],
    ageRange: [4, 7],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 creative child inventor',
    auxiliaryCharacters: 'Helpful family members, imaginary space friends',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },

  // UNDERWATER (4)
  {
    id: 'mermaid-friend',
    title: 'My Mermaid Friend',
    plot: 'While playing at the beach, a child meets a shy young mermaid hiding in the tide pools. They become friends and learn about each other\'s worlds—one above water, one below.',
    category: 'underwater',
    themes: ['friendship', 'ocean', 'mermaids', 'discovery'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'magical_creature'],
    mainCharacterCount: 2,
    mainCharacterRoles: '1 child + 1 mermaid',
    auxiliaryCharacters: 'Sea creatures, shells, waves',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'submarine-adventure',
    title: 'Submarine Adventure',
    plot: 'A child takes an imaginary submarine ride to explore the ocean floor. They discover colorful coral, glowing fish, and a friendly octopus who shows them the wonders of the deep sea.',
    category: 'underwater',
    themes: ['ocean', 'exploration', 'marine life', 'imagination'],
    ageRange: [4, 7],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 child explorer',
    auxiliaryCharacters: 'Friendly octopus, colorful fish, coral',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'dolphin-rescue',
    title: 'Dolphin Rescue',
    plot: 'A child on vacation sees a dolphin trapped in fishing net near the beach. Working with lifeguards and marine biologists, they help free the dolphin and learn about protecting ocean life.',
    category: 'underwater',
    themes: ['conservation', 'helping animals', 'teamwork', 'ocean'],
    ageRange: [5, 8],
    characterTypeHints: ['human', 'animal'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 brave child hero',
    auxiliaryCharacters: 'Dolphin, lifeguards, marine biologist',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
  {
    id: 'sea-shell-collection',
    title: 'The Magical Seashell Collection',
    plot: 'A child collects seashells at the beach. That night, they discover each shell holds a story from the ocean. A clam shell tells of quiet patience, a conch tells of the ocean\'s song.',
    category: 'underwater',
    themes: ['seashells', 'stories', 'beach', 'listening'],
    ageRange: [4, 7],
    characterTypeHints: ['human'],
    mainCharacterCount: 1,
    mainCharacterRoles: '1 curious shell collector',
    auxiliaryCharacters: 'Talking seashells, memories of ocean creatures',
    recommendedIllustrations: 8,
    recommendedPages: 16,
    aiOptimized: true,
  },
]

/**
 * Get plot suggestions filtered by age
 */
export function getFilteredPlotSuggestions(
  age?: number,
  category?: string,
): StoryPlotSuggestion[] {
  let filtered = storyPlotSuggestions

  if (age !== undefined) {
    filtered = filtered.filter((s) => age >= s.ageRange[0] && age <= s.ageRange[1])
  }

  if (category) {
    filtered = filtered.filter((s) => s.category === category)
  }

  return filtered
}
